import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, Handshake, Building2, Users, Award, ChevronLeft, ChevronRight } from "lucide-react";
import Footer from "../../Components/Footer";
import { useLanguage } from "../../contexts/LanguageContext";
import { partners } from "../../data/collaborationsData";
export default function CollaborationsSection({ onNavigate, onOpenPrivacyPolicy, onOpenCookiePolicy, onOpenTermsOfService }) {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  /* partners imported from ../data/collaborationsData */

  const partnersPerSlide = 3;
  const totalSlides = Math.ceil(partners.length / partnersPerSlide);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  const getCurrentPartners = () => {
    const start = currentSlide * partnersPerSlide;
    return partners.slice(start, start + partnersPerSlide);
  };

  return (
    <>
      <section className="min-h-screen py-24 md:py-32 bg-[--bg-primary] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[--accent] rounded-full opacity-5 blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 rounded-full opacity-5 blur-3xl" />
        </div>

        <motion.div
          variants={containerAnimation}
          initial="hidden"
          animate="visible"
          className="max-w-7xl mx-auto px-6 relative z-10"
        >
          {/* Header */}
          <motion.div variants={itemAnimation} className="text-center mb-16">
            <motion.div className="inline-flex items-center justify-center mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
              <span className="text-[--accent] text-sm font-medium tracking-[0.3em] uppercase mx-6">
                03
              </span>
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
            </motion.div>

            <h2 className="text-4xl md:text-6xl font-display font-bold text-[--text-primary] mb-8 leading-tight">
              {t.collaborations.title}
            </h2>
          </motion.div>

          {/* Introduction */}
          <motion.div variants={itemAnimation} className="text-center mb-20">
            <div className="glass rounded-3xl p-8 md:p-12 max-w-4xl mx-auto">
              <div className="flex items-center justify-center mb-6">
                <Handshake className="w-12 h-12 text-[--accent] mr-4" />
                <h3 className="text-2xl md:text-3xl font-display font-semibold text-[--text-primary]">
                  {t.collaborations.sectionTitle}
                </h3>
              </div>
              <p className="text-lg md:text-xl text-[--text-secondary] leading-relaxed mb-4">
                {t.collaborations.intro}
              </p>
            </div>
          </motion.div>

          {/* Partners Section */}
          <motion.div variants={itemAnimation}>
            <h3 className="text-3xl md:text-4xl font-display font-bold text-[--text-primary] mb-12 text-center">
              {t.collaborations.ourPartners.split(' ')[0]} {t.collaborations.ourPartners.split(' ')[1]} <span className="gradient-text">{t.collaborations.ourPartners.split(' ').slice(2).join(' ')}</span>
            </h3>

            {/* Modern Partners Slider */}
            <div className="relative max-w-6xl mx-auto">
              {/* Slider Container */}
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[--bg-secondary]/50 to-[--bg-primary]/50 backdrop-blur-sm border border-[--accent]/10">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 300 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -300 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="grid grid-cols-1 md:grid-cols-3 gap-8 p-8 md:p-12"
                  >
                    {getCurrentPartners().map((partner, index) => (
                      <motion.a
                        key={`${currentSlide}-${index}`}
                        href={partner.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ y: -8, scale: 1.02 }}
                        onMouseEnter={() => setIsAutoPlaying(false)}
                        onMouseLeave={() => setIsAutoPlaying(true)}
                      >
                        <div className="relative glass rounded-3xl p-8 h-full hover:shadow-luxury-hover transition-all duration-500 border border-transparent hover:border-[--accent]/30 overflow-hidden">
                          {/* Background gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-br from-[--accent]/5 via-transparent to-[--accent]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                          {/* Decorative corner accent */}
                          <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-[--accent]/20 to-transparent rounded-bl-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                          {/* Partner Logo */}
                          <div className="relative aspect-square mb-6 rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm">
                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                            <img
                              src={partner.image}
                              alt={partner.name}
                              className="w-full h-full object-contain p-6 transition-all duration-500 group-hover:scale-110 group-hover:brightness-110"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            {/* Fallback placeholder */}
                            <div className="w-full h-full bg-gradient-to-br from-[--accent]/20 to-[--accent]/40 flex items-center justify-center hidden">
                              <div className="text-center">
                                <Handshake className="w-12 h-12 text-[--accent] mx-auto mb-2" />
                                <p className="text-white font-medium text-sm">{partner.name}</p>
                              </div>
                            </div>
                            {/* Logo overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-[--accent]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                          </div>

                          {/* Partner Info */}
                          <div className="text-center relative z-10">
                            <h4 className="text-xl font-display font-bold text-[--text-primary] mb-3 group-hover:text-[--accent] transition-colors duration-300">
                              {partner.name}
                            </h4>

                            <p className="text-[--text-secondary] text-sm leading-relaxed mb-4 group-hover:text-[--text-primary] transition-colors duration-300">
                              {t.collaborations[partner.descriptionKey]}
                            </p>

                            {/* Visit Button */}
                            <div className="flex items-center justify-center space-x-2 bg-[--accent]/10 rounded-2xl py-3 px-4 group-hover:bg-[--accent]/20 transition-colors duration-300">
                              <span className="text-[--accent] font-semibold text-sm">{t.collaborations.visitSite}</span>
                              <ExternalLink className="w-4 h-4 text-[--accent] group-hover:translate-x-1 transition-transform duration-300" />
                            </div>
                          </div>

                          {/* Bottom accent line */}
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[--accent] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        </div>
                      </motion.a>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[--bg-primary]/80 backdrop-blur-sm border border-[--accent]/20 flex items-center justify-center text-[--accent] hover:bg-[--accent] hover:text-white transition-all duration-300 shadow-lg z-10"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[--bg-primary]/80 backdrop-blur-sm border border-[--accent]/20 flex items-center justify-center text-[--accent] hover:bg-[--accent] hover:text-white transition-all duration-300 shadow-lg z-10"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Slide Indicators */}
              <div className="flex justify-center mt-8 space-x-3">
                {Array.from({ length: totalSlides }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide
                      ? 'bg-[--accent] scale-125 shadow-lg'
                      : 'bg-[--accent]/30 hover:bg-[--accent]/60'
                      }`}
                  />
                ))}
              </div>

              {/* Auto-play indicator */}
              <div className="absolute top-4 right-4 flex items-center space-x-2 bg-[--bg-primary]/80 backdrop-blur-sm rounded-full px-3 py-1">
                <div className={`w-2 h-2 rounded-full ${isAutoPlaying ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`}></div>
                <span className="text-xs text-[--text-secondary]">
                  {isAutoPlaying ? t.collaborations.auto : t.collaborations.manual}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div variants={itemAnimation} className="text-center mt-20">
            <div className="glass rounded-2xl p-8 max-w-2xl mx-auto">
              <h4 className="text-xl font-display font-semibold text-[--text-primary] mb-4">
                {t.collaborations.interestedTitle}
              </h4>
              <p className="text-[--text-secondary] mb-6">
                {t.collaborations.interestedDesc}
              </p>
              <motion.button
                onClick={() => {
                  // Navigate to contacts section
                  const contactsSection = document.getElementById('contacts');
                  if (contactsSection) {
                    contactsSection.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    // If using React Router or similar navigation
                    window.location.hash = '#contacts';
                  }
                }}
                className="inline-flex items-center text-[--accent] hover:text-[--accent-hover] transition-colors cursor-pointer group"
                whileHover={{ scale: 1.05 }}
              >
                <span className="font-medium mr-2">{t.collaborations.discoverMore}</span>
                <div className="w-8 h-px bg-[--accent] group-hover:w-12 transition-all" />
              </motion.button>
            </div>
          </motion.div>
        </motion.div>

        {/* Gradient Footer */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[--bg-secondary] via-[--bg-secondary]/60 to-transparent pointer-events-none" />
      </section>

      <Footer
        onNavigate={onNavigate}
        onOpenPrivacyPolicy={onOpenPrivacyPolicy}
        onOpenCookiePolicy={onOpenCookiePolicy}
        onOpenTermsOfService={onOpenTermsOfService}
      />
    </>
  );
}
